import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import './Auth.css';

// Mirrors the server-side policy in POST /api/auth/reset-password. Kept here
// only to give immediate feedback — the server is the one that enforces it.
const RULES = [
  { key: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'number', label: 'One number', test: (pw) => /[0-9]/.test(pw) }
];

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('validating'); // validating | form | invalid | success
  const [account, setAccount] = useState(null);
  const [message, setMessage] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const checkedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage('No reset token was provided. Please use the link from your email.');
      return;
    }

    if (checkedRef.current) return;
    checkedRef.current = true;

    api.get(`/api/auth/reset-password/${token}`)
      .then((data) => {
        setAccount({ username: data.username, firstName: data.firstName });
        setStatus('form');
      })
      .catch((err) => {
        setStatus('invalid');
        setMessage(
          err.response?.data?.error?.message ||
          'This reset link is invalid or has expired. Ask an administrator to send a new one.'
        );
      });
  }, [token]);

  const rulesMet = useMemo(
    () => RULES.map(rule => ({ ...rule, met: rule.test(newPassword) })),
    [newPassword]
  );

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = rulesMet.every(r => r.met) && passwordsMatch && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!rulesMet.every(r => r.met)) {
      setError('Please meet all the password requirements.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await api.post('/api/auth/reset-password', {
        token,
        newPassword,
        confirmPassword
      });
      setMessage(data.message || 'Your password has been reset.');
      setStatus('success');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const eyeIcon = showPassword ? (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
    </svg>
  );

  const lockIcon = (
    <svg className="input-icon" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
    </svg>
  );

  const backArrow = (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
    </svg>
  );

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/hd-plus-rcm-logo.png" alt="HD Plus RCM Logo" className="auth-logo-img" />
          {status === 'form' && <p>Choose a new password</p>}
        </div>

        {status === 'validating' && (
          <div className="success-card-content">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div className="loading-spinner" />
            </div>
            <h2>Checking your link...</h2>
            <p>Please wait a moment.</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="success-card-content">
            <div className="success-icon" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h2>Link No Longer Valid</h2>
            <p>{message}</p>
            <Link to="/login" className="back-to-login">
              {backArrow}
              Back to Sign In
            </Link>
          </div>
        )}

        {status === 'success' && (
          <div className="success-card-content">
            <div className="success-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2>Password Reset</h2>
            <p>{message}</p>
            <Link to="/login" className="back-to-login">
              {backArrow}
              Continue to Sign In
            </Link>
          </div>
        )}

        {status === 'form' && (
          <>
            {account && (
              <div className="auth-info" style={{ marginBottom: 20 }}>
                <p>Setting a new password for <strong>{account.username}</strong>.</p>
              </div>
            )}

            {error && (
              <div className="auth-error" style={{ marginBottom: 20 }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="input-group">
                <label htmlFor="newPassword">New password</label>
                <div className="input-wrapper">
                  {lockIcon}
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    className="auth-input"
                    placeholder="Enter a new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {eyeIcon}
                  </button>
                </div>
              </div>

              <ul className="password-rules">
                {rulesMet.map(rule => (
                  <li key={rule.key} className={rule.met ? 'met' : ''}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      {rule.met
                        ? <polyline points="20 6 9 17 4 12" />
                        : <circle cx="12" cy="12" r="4" />}
                    </svg>
                    {rule.label}
                  </li>
                ))}
              </ul>

              <div className="input-group">
                <label htmlFor="confirmPassword">Confirm new password</label>
                <div className="input-wrapper">
                  {lockIcon}
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    className="auth-input"
                    placeholder="Re-enter the new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <span className="password-mismatch">Passwords do not match</span>
                )}
              </div>

              <button type="submit" className="auth-button" disabled={!canSubmit}>
                {submitting && <span className="btn-spinner" />}
                {submitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>

            <div className="auth-footer">
              <p><Link to="/login">Back to sign in</Link></p>
            </div>
          </>
        )}
      </div>

      <div className="auth-page-footer">
        <p>&copy; 2026 Revenue Cycle Management. All rights reserved.</p>
      </div>
    </div>
  );
}

export default ResetPassword;
